import React, { useEffect, useRef, useState } from "react";

/**
 * Isolated export progress panel with smooth display progress between coarse
 * server updates (e.g. 0 → 45 → 100).
 */
export default function ExportProgressBar({ active, message, progress = 0 }) {
    const [display, setDisplay] = useState(0);
    const targetRef = useRef(0);

    useEffect(() => {
        if (!active) {
            targetRef.current = 0;
            setDisplay(0);
            return;
        }
        const next = Math.max(0, Math.min(100, Number(progress) || 0));
        if (next > targetRef.current) {
            targetRef.current = next;
        }
    }, [active, progress]);

    useEffect(() => {
        if (!active) {
            return undefined;
        }
        const tick = () => {
            setDisplay((current) => {
                const target = targetRef.current;
                if (current < target) {
                    const gap = target - current;
                    const step = Math.max(0.5, gap * 0.06);
                    return Math.min(target, current + step);
                }
                if (target < 100 && current < 94) {
                    const ceiling = Math.min(94, target + 12);
                    if (current < ceiling) {
                        return Math.min(ceiling, current + 0.3);
                    }
                }
                return current;
            });
        };
        const id = window.setInterval(tick, 50);
        return () => window.clearInterval(id);
    }, [active]);

    if (!active) {
        return null;
    }

    const pct = Math.round(display);
    const width = Math.max(0, Math.min(100, display));

    return (
        <div
            className="relative z-30 shrink-0 border-b border-emerald-500/25 bg-zinc-950 shadow-[0_10px_32px_rgba(0,0,0,0.75)]"
            role="status"
            aria-live="polite"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="export-progress-panel"
        >
            <div className="px-6 py-3">
                <div className="flex items-center justify-between gap-4 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-200/90">
                    <span className="truncate">{message || "Preparing export…"}</span>
                    <span className="tabular-nums text-emerald-300">{pct}%</span>
                </div>
                <div
                    className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-800 ring-1 ring-white/10"
                    data-testid="export-progress-track"
                >
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400"
                        style={{
                            width: `${width}%`,
                            transition: "width 80ms linear",
                        }}
                        data-testid="export-progress-fill"
                    />
                </div>
            </div>
        </div>
    );
}
